import React, { PureComponent } from "react";
import { Chart, ChartProps, ITable, ColorProps, GlobalColorPalette, Color,
  renderTooltip, getRangeByIndex, getNumbersByStrings, ChartStyleProps } from "@gago/frame";
import styled from "styled-components";
import ReactEcharts from "echarts-for-react";
// tslint:disable: variable-name naming-convention ter-max-len

const Root = styled.div`
  width: 100%;
  height: 100%;
  .layer-base{
    width: 100%;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
  }
  .layer-float{
    width: 100%;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }
`;

type Props = ChartProps<1, 1> & ColorProps<GlobalColorPalette> & ChartStyleProps;

/**
 * 雷达图
 * @author 马俊峰
 * @date 2020-07-31
 * @export
 * @class RadarChart
 * @extends {PureComponent<Props>}
 * @implements {Chart<1, 1>}
 * @implements {Color}
 */
export class RadarChart extends PureComponent< Props> implements Chart<1, 1>, Color {

  render() {
    const { chartData } = this.props;
    const indicatorList = getRangeByIndex(chartData, 1);
    const datalist = getNumbersByStrings(chartData, [undefined]);
    // 这里可以确定过滤后部位undefined
    const maxNum = Math.max(...datalist.filter(item => item !== undefined) as unknown as number[]);
    const option = this.themeDarkOption(indicatorList, datalist, maxNum);
    const layerOption = this.themeDarkLayerOption(indicatorList, datalist, maxNum);
    return (
      <Root>
        <ReactEcharts notMerge className="layer-base" option={option} />
        <ReactEcharts notMerge className="layer-float" option={layerOption} />
      </Root>
    );
  }

  /**
   * 提示框
   *
   * @author 张卓诚
   * @private
   * @param {ITable<1, 1>} chartData
   * @param {any[]} axisList
   * @returns
   * @memberof RadarChart
   */
  private getTooltipOption(chartData: ITable<1, 1>, axisList: any[]) {
    const { chartStyle } = this.props;
    const { suffix } = chartData;
    return {
      backgroundColor: "rgba(0, 0, 0, 0.65)",
      textStyle: {
        align: "left",
        fontSize: 12,
        color: chartStyle.tooltipMainTextColor,
      },
      extraCssText: "box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.2)", // 添加阴影
      formatter: (params: any) => {
        let detail = "";
        const valueList = params.value || [];
        // tslint:disable:prefer-for-of
        for (let index = 0; index < valueList.length; index++) {
          const element = valueList[index];
          detail = detail + `
          <div style="font-size: 12px">
              <span>${axisList[index]}：${element && element || "0"} ${suffix && suffix[2]}</span>
          </div>`;
        }
        return detail;
      },
    };
  }

  /** 暗色主题第一层echart 配置 */
  private themeDarkOption = (axisList: any[], dataList: any[], maxNum: number) => {
    const { chartData, colorPalette, chartStyle } = this.props;
    const darkOption = {
      color: colorPalette.subColor,
      tooltip: {
        ...this.getTooltipOption(chartData, axisList),
        show: true,
        confine: true,
        trigger: "item",
      },
      radar: {
        center: ["50%", "50%"],
        radius: "70%",
        startAngle: 90,
        splitNumber: 4,
        shape: "polygon",
        splitArea: {
          areaStyle: {
            color: "transparent",
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: chartStyle.mainAxisColor,
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: [chartStyle.mainAxisColor, "transparent"],
            type: "solid",
          },
        },
        indicator: axisList.map(item => ({ text: item, max: maxNum, color: chartStyle.mainAxisColor })),
      },
      series: [
        {
          name: chartData.head[2],
          type: "radar",
          symbol: "circle",
          symbolSize: 3,
          itemStyle: {
            borderWidth: 4,
            borderColor: colorPalette.subColor[0],
            shadowColor: colorPalette.subColor[0],
            shadowBlur: 3,
          },
          lineStyle: {
            normal: {
              color: colorPalette.subColor[0],
              width: 0.8,
            },
          },
          areaStyle: {
            normal: {
              color: colorPalette.subColor[0],
              opacity: 0.3,
            },
          },
          data: [
            {
              value: dataList,
            },
          ],
        },
      ],
    };
    return darkOption;
  }

  /** 暗色主题第二层echart配置 */
  private themeDarkLayerOption = (axisList: any[], dataList: any[], maxNum: number) => {
    const { chartData, colorPalette, chartStyle } = this.props;
    const darkLayerOption = {
      color: colorPalette.subColor,
      tooltip: {
        ...this.getTooltipOption(chartData, axisList),
        show: false,
        confine: true,
        trigger: "item",
      },
      radar: {
        center: ["50%", "50%"],
        radius: "70%",
        startAngle: 90,
        splitNumber: 4,
        shape: "polygon",
        splitArea: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: ["transparent", chartStyle.splitLineColor],
            type: "dashed",
          },
        },
        indicator: axisList.map(item => ({ text: item, max: maxNum, color: "transparent" })),
      },
      series: [
        {
          name: chartData.head[2],
          type: "radar",
          symbol: "circle",
          symbolSize: 2,
          itemStyle: {
            borderWidth: 3,
            borderColor: "transparent",
            shadowColor: "transparent",
            shadowBlur: 3,
          },
          lineStyle: {
            normal: {
              color: "transparent",
            },
          },
          areaStyle: {
            normal: {
              color: "transparent",
            },
          },
          data: [{ value: dataList }],
        },
      ],
    };
    return darkLayerOption;
  }
}
// tslint:disable-next-line: max-file-line-count
